/**
 * Forward + reverse geocoding via the public OpenStreetMap Nominatim
 * endpoint. Used by the Marketplace and Bins pages so typing a UK city
 * or postcode into the search bar flies the map to that location.
 *
 * Nominatim's usage policy asks callers to add a `User-Agent` and cap
 * request rate — we defensively debounce at the call site (600ms
 * per keystroke) and bias results to GB to keep responses relevant.
 */

const NOMINATIM = "https://nominatim.openstreetmap.org";

/**
 * Forward geocode a free-text query to `{lat, lng, label}` or `null`.
 * `countrycodes=gb` limits results to the UK so a search for "york"
 * doesn't jump to New York, USA.
 */
export async function forwardGeocode(query) {
  const q = (query || "").trim();
  if (q.length < 3) return null;
  try {
    const url =
      `${NOMINATIM}/search?format=json&limit=1&countrycodes=gb&addressdetails=1&q=${encodeURIComponent(q)}`;
    const r = await fetch(url, {
      headers: { "Accept-Language": "en-GB" },
    });
    if (!r.ok) return null;
    const data = await r.json();
    const hit = Array.isArray(data) && data[0];
    if (!hit) return null;
    return {
      lat: parseFloat(hit.lat),
      lng: parseFloat(hit.lon),
      label: hit.display_name || q,
    };
  } catch {
    return null;
  }
}

/**
 * Reverse geocode coordinates to a human-friendly place name.
 * Returns "" on failure — call sites treat empty string as "unknown area".
 */
export async function reverseGeocode(lat, lng) {
  try {
    const r = await fetch(
      `${NOMINATIM}/reverse?format=json&lat=${lat}&lon=${lng}&zoom=10&addressdetails=1`,
      { headers: { "Accept-Language": "en-GB" } }
    );
    const data = await r.json();
    const a = data.address || {};
    return (
      a.city ||
      a.town ||
      a.village ||
      a.county ||
      a.state ||
      data.display_name ||
      ""
    );
  } catch {
    return "";
  }
}
